import { SaleOrder, SaleOrderAdd, SaleItemState, SaleReportHisItemAdd, SaleReportHisItem, SaleOrderAddItem, SaleOrderItemState } from "@/types/sales";
import { isArray, isSaleOrder, isString, isStringEmpty } from "@/utils/checkTool";
import request from ".";
import { cleanToSaleOrder } from "@/utils/cleanData";
import { CommonListParams, CommonStateEnum } from "@/types/common";
import { getStorage } from "@/utils/storage";
import { DictLike } from "@/types/dict";
import { Requirement, RequirementStatusEnum } from "@/types/requirement";

const prefixUrl = "/wms/sale";
const itemUrl = "/wms/sale/item";

const apis = {
  add: prefixUrl + `/add`,
  sAdd: prefixUrl + `/s/add`,
  get: (id: string) => prefixUrl + `/${id}/get`,
  fetch: (id: string) => prefixUrl + `/${id}/fetch`,
  itemFetch: (id: string) => prefixUrl + `/item/${id}/fetch`,
  itemGet: (id: string) => prefixUrl + `/item/${id}/get`,
  del: (id: string) => prefixUrl + `/${id}/del`,
  list: prefixUrl + `/list`,
  fetchs: prefixUrl + `/fetchs`,
  fetchsOffical: prefixUrl + `/1/fetchs`,
  orderFetch: (id: string) => prefixUrl + `/${id}/fetch`,
  update: (id: string) => prefixUrl + `/${id}/update`,
  updateFinishState: (id: string) => prefixUrl + `/${id}/finishState/update`,
  updateByKey: (id: string, key: OrderUpdateKey) => prefixUrl + `/${id}/${key}/update`,
  orderTypes: prefixUrl + `/order/types`,
  approvalList: (preApprovalState: CommonStateEnum, requirement?: CommonStateEnum) => `/wms/sale/${preApprovalState}${requirement !== undefined ? '/' + requirement : ''}/item/list`,
  approvalOrderList: (preApprovalState: CommonStateEnum, requirement?: CommonStateEnum) => `/wms/sale/${preApprovalState}${requirement !== undefined ? '/' + requirement : ''}/fetchs`,

  itemAdd: (id: string) => prefixUrl + '/' + id + '/item/add',
  itemDel: (id: string) => itemUrl + `/${id}/del`,
  itemUpdate: (id: string) => itemUrl + `/${id}/update`,
  itemUpdateByKey: (id: string, key: OrderUpdateKey) => prefixUrl + `/${id}/${key}/update`,
  itemList: itemUrl + '/list',
  itemListOffical: prefixUrl + '/1/item/list',
  itemStateList: itemUrl + '/state/list',
  itemAddProState: (id: string) => itemUrl + `/${id}/prodState/his/add`,
  itemUpdateProState: (id: string) => itemUrl + `/prodState/${id}/his/update`,
  itemDelProState: (id: string) => itemUrl + `/prodState/his/${id}/del`,
};

type OrderUpdateKey =
  'type'
  | 'progress'
  | "receiver"
  | "receiverContact"
  | "receiverMobile"
  | 'receiverAddress'
  | 'totalAmount'
  | 'realAmount'
  | 'country'
  | 'taxRate'
  | 'innerNumber'
  | 'preApproval'
  | 'otherNumber1'
  | 'bomFileId'
  | 'otherNumber1';

export type OrderItemUpdateKey =
  'priceId'
  | "info"
  | "quantity"
  | "amount"
  | 'reamAmount'
  | 'remark'
  | 'mmfgr'
  | 'customerModel'
  | 'productName'
  | 'reqDeliDate'
  | 'smtStartDate'
  | 'preFinishDate'
  | 'urgencyLevel'
  | 'specialReq'

export const add = (addInfo: SaleOrderAdd) => {
  return new Promise<SaleOrderItemState>((resolve, reject) => {
    if (addInfo.type && !isString(addInfo.type)) {
      return reject(10012);
    }
    return request<SaleOrderItemState, SaleOrderAdd>(apis.add, {
      params: { body: addInfo },
      requestConfig: { headers: { org_id: getStorage('curOrgId') } }
    })
      .then(({ body: info }) => resolve(info))
      .catch((err) => reject(err));
  })
};

export const sAdd = (addInfo: SaleOrderAdd, sOrgid: string, supplierApi: string) => {
  return new Promise<SaleOrderItemState>((resolve, reject) => {
    if (addInfo.type && !isString(addInfo.type)) {
      return reject(10012);
    }
    return request<SaleOrderItemState, SaleOrderAdd>(apis.sAdd, {
      params: { body: addInfo },
      requestConfig: { headers: { org_id: sOrgid }, baseURL: supplierApi }
    })
      .then(({ body: info }) => resolve(info))
      .catch((err) => reject(err));
  })
};

export const get = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<SaleOrder, string>(apis.get(id))
    .then(({ body: info }) => {
      if (isSaleOrder(info)) {
        return Promise.resolve(cleanToSaleOrder(info));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const fetch = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<SaleOrder, string>(apis.fetch(id))
    .then(({ body: info }) => {
      if (isSaleOrder(info)) {
        return Promise.resolve(cleanToSaleOrder(info));
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};

export const itemFetch = (id: string, controller?: AbortController) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<SaleItemState, string>(apis.itemFetch(id), { controller })
    .then(({ body: info }) => Promise.resolve(info))
    .catch((err) => Promise.reject(err));
};

export const itemGet = (id: string) => {
  if (!isString(id) || isStringEmpty(id)) {
    return Promise.reject(10012);
  }
  return request<SaleItemState, string>(apis.itemGet(id))
    .then(({ body: info }) => Promise.resolve(info))
    .catch((err) => Promise.reject(err));
};

export const del = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<void, void>(apis.del(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const list = (params?: CommonListParams<string>) => {
  if (params && params.body && !isString(params.body)) {
    return Promise.reject(10012);
  }
  return request<Array<SaleOrder>, string>(apis.list, { params })
    .then(({ body: info, pagination }) => {
      if (isArray<SaleOrder>(info)) {
        return Promise.resolve({
          body: info
            .filter((info) => isSaleOrder(info))
            ?.map((info) => cleanToSaleOrder(info)),
          pagination,
        });
      } else {
        return Promise.reject(10013);
      }
    })
    .catch((err) => Promise.reject(err));
};
export interface SaleListParams {
  keyword?: string;
}
export const fetchsWithOrg = (params?: CommonListParams<SaleListParams>, orgId?: string) => {
  return request<Array<SaleOrderItemState>, SaleListParams>(apis.fetchsOffical, {
    params,
    requestConfig: orgId ? { headers: { org_id: orgId } } : undefined,
  })
    .then(({ body: info, pagination }) => {
      return Promise.resolve({
        body: info,
        pagination,
      });
    })
    .catch((err) => Promise.reject(err));
};

interface ApprovalListProps {
  state: CommonStateEnum;
  params?: CommonListParams<{ customerId: string, keyword: string, requireStatus?: RequirementStatusEnum }>;
  requirement?: CommonStateEnum;
  orgId?: string;
}

export const approvalList = ({ state, params, requirement, orgId }: ApprovalListProps) => {
  return new Promise<CommonListParams<Array<SaleItemState>>>((resolve, reject) => {
    return request<Array<SaleItemState>, { customerId: string, keyword: string }>(apis.approvalList(state, requirement), { params, requestConfig: orgId ? { headers: { 'Org_Id': orgId } } : {} })
      .then(({ body, pagination }) => {
        if (isArray<SaleItemState>(body)) {
          return resolve({
            body,
            pagination,
          });
        } else {
          return reject(10013);
        }
      })
      .catch((err) => reject(err));
  })
};

export const approvalOrderList = ({ state, params, requirement, orgId }: ApprovalListProps) => {
  return new Promise<CommonListParams<Array<SaleOrderItemState>>>((resolve, reject) => {
    return request<Array<SaleOrderItemState>, { customerId: string, keyword: string }>(apis.approvalOrderList(state, requirement), { params, requestConfig: orgId ? { headers: { 'Org_Id': orgId } } : {} })
      .then(({ body, pagination }) => {
        if (isArray<SaleOrderItemState>(body)) {
          return resolve({
            body,
            pagination,
          });
        } else {
          return reject(10013);
        }
      })
      .catch((err) => reject(err));
  })
};

export const fetchs = (params?: CommonListParams) => {
  return new Promise<CommonListParams<Array<SaleOrderItemState>>>((resolve, reject) => {
    if (params && params.body && !isString(params.body)) {
      return reject(10012);
    }
    return request<Array<SaleOrderItemState>, string>(apis.fetchs, { params })
      .then(({ body, pagination }) => {
        return resolve({
          body,
          pagination,
        });
      })
      .catch((err) => reject(err));
  })
};
export const orderFetch = (id: string) => {
  return new Promise<SaleOrderItemState>((resolve, reject) => {
    request<SaleOrderItemState, string>(apis.orderFetch(id))
      .then(({ body }) => {
        return resolve(body);
      })
      .catch((err) => reject(err));
  })
};

export const update = (id: string, info: SaleOrderAdd) => {
  return new Promise<SaleOrderItemState>((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id))
    ) {
      return reject(10012);
    }
    request<SaleOrderItemState, SaleOrderAdd>(apis.update(id), {
      params: { body: info },
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

// G03.D06.M11.I55：更新销售订单完结状态，0 未完结，1 已完结。
export const updateFinishState = (id: string, finishState: 0 | 1) => {
  if (!isString(id) || isStringEmpty(id) || (finishState !== 0 && finishState !== 1)) {
    return Promise.reject(10012);
  }
  return request<SaleOrder, 0 | 1>(apis.updateFinishState(id), {
    params: { body: finishState },
  }).then(({ body }) => body);
};

export interface UpdateByKey {
  key: OrderUpdateKey;
  value: string | number | undefined;
}

export const updateByKey = (id: string, props: UpdateByKey) => {
  return new Promise<void>((resolve, reject) => {
    if (
      (!isString(id) || isStringEmpty(id)) &&
      props.key &&
      !isString(props.key)
    ) {
      return reject(10012);
    }
    request<void, string | number>(apis.updateByKey(id, props.key), {
      params: { body: props.value },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const orderTypes = () => {
  return new Promise<Array<DictLike>>((resolve, reject) => {
    request<Array<DictLike>, string>(apis.orderTypes)
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  });
};

export const itemList = (params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<SaleItemState>>>((resolve, reject) => {
    if (params && params.body && !isString(params.body)) {
      return reject(10012);
    }
    return request<Array<SaleItemState>, string>(apis.itemList, { params })
      .then(({ body, pagination }) => {
        if (isArray<SaleItemState>(body)) {
          return resolve({
            body,
            pagination,
          });
        } else {
          return reject(10013);
        }
      })
      .catch((err) => reject(err));
  })
};

export const itemListWithOrg = (params?: CommonListParams<SaleListParams>, orgId?: string) => {
  return new Promise<CommonListParams<Array<SaleItemState>>>((resolve, reject) => {
    return request<Array<SaleItemState>, SaleListParams>(apis.itemListOffical, {
      params,
      requestConfig: orgId ? { headers: { org_id: orgId } } : undefined,
    })
      .then(({ body, pagination }) => {
        if (isArray<SaleItemState>(body)) {
          return resolve({
            body,
            pagination,
          });
        } else {
          return reject(10013);
        }
      })
      .catch((err) => reject(err));
  })
};

export const itemStateList = (params?: CommonListParams<string>) => {
  return new Promise<CommonListParams<Array<SaleItemState>>>((resolve, reject) => {
    if (params && params.body && !isString(params.body)) {
      return reject(10012);
    }
    return request<Array<SaleItemState>, string>(apis.itemStateList, { params })
      .then(({ body, pagination }) => {
        if (isArray<SaleItemState>(body)) {
          return resolve({
            body,
            pagination,
          });
        } else {
          return reject(10013);
        }
      })
      .catch((err) => reject(err));
  })
};

export const itemAdd = (id: string, info: SaleOrderAddItem) => {
  return new Promise<SaleItemState>((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id))
    ) {
      return reject(10012);
    }
    request<SaleItemState, SaleOrderAddItem>(apis.itemAdd(id), {
      params: { body: info },
    })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  });
};

export const itemDel = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    if (!isString(id) || isStringEmpty(id)) {
      return Promise.reject(10012);
    }
    request<void, void>(apis.itemDel(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};
export const itemUpdate = (id: string, info: SaleOrderAddItem) => {
  return new Promise<void>((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id))
    ) {
      return reject(10012);
    }
    request<void, SaleOrderAddItem>(apis.itemUpdate(id), {
      params: { body: info },
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  });
};

export const saleItemUpdate = (id: string, info: SaleOrderAddItem) => {
  return new Promise<SaleItemState>((resolve, reject) => {
    if (
      !isString(id) ||
      (isStringEmpty(id))
    ) {
      return reject(10012);
    }
    request<SaleItemState, SaleOrderAddItem>(apis.itemUpdate(id), {
      params: { body: info },
    })
      .then((res) => resolve(res.body))
      .catch((err) => reject(err));
  });
};

export const itemAddProState = (id: string, props: SaleReportHisItemAdd) => {
  return new Promise<SaleReportHisItem>((resolve, reject) => {
    return request<SaleReportHisItem, SaleReportHisItemAdd>(apis.itemAddProState(id), {
      params: { body: props }
    })
      .then(({ body }) => resolve(body))
      .catch((err) => reject(err));
  })
}

export const itemUpdateProState = (id: string, props: SaleReportHisItemAdd) => {
  return new Promise<void>((resolve, reject) => {
    return request<Array<SaleReportHisItem>, SaleReportHisItemAdd>(apis.itemUpdateProState(id), {
      params: { body: props }
    })
      .then(() => resolve())
      .catch((err) => reject(err));
  })
}

export const itemDelProState = (id: string) => {
  return new Promise<void>((resolve, reject) => {
    return request<void, void>(apis.itemDelProState(id))
      .then(() => resolve())
      .catch((err) => reject(err));
  })
}

const exportObject = {
  list,
  fetchsWithOrg,
  fetchs,
  add,
  del,
  get,
  fetch,
  updateByKey,
  update,
  orderTypes,
  approvalList,
  approvalOrderList,
  itemStateList,
  itemAddProState,
  itemUpdateProState,
  itemDelProState,
  itemList,
  itemListWithOrg,
  itemAdd,
  itemUpdate,
  saleItemUpdate,
  itemDel
};

export type { OrderUpdateKey, ApprovalListProps };

export default exportObject;
